import React, { useEffect, useState } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, TimeScale } from "chart.js";
import { Line } from "react-chartjs-2";
import { fetchReportShelterIntakesExits } from "../../../services/httpClient";
import "chartjs-adapter-date-fns";
import { loadingCard, wait } from "../../../helpers/loading";

ChartJS.register(TimeScale, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface ShelterOccupancyLineChartState {
  data?: any[];
  startDate?: string;
  endDate?: string;
  useDatePicker?: boolean;
}

export const ShelterOccupancyLineChart = ({ data, startDate, endDate, useDatePicker = false }: ShelterOccupancyLineChartState) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [current, setCurrent] = useState<any[]>([]);
  const [numberDays, setNumberDays] = useState<number>(30);

  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      await wait(250); // Simulate loading delay
      if (data) {
        let days = 30;
        if (startDate && endDate) {
          days = await daysBetween(startDate, endDate);
        }
        setNumberDays(days);
        const chartData = getOccupancyPerDay(data, days);
        setCurrent(chartData);
        setIsLoading(false);
      } else {
        const starting = new Date();
        starting.setDate(starting.getDate() - 365);
        let start = starting.toISOString().slice(0, 10);
        let end = new Date().toISOString().slice(0, 10);
        if (useDatePicker) {
          start = startDate ?? start;
          end = endDate ?? end;
          let days = await daysBetween(start, end);
          setNumberDays(days);
          await getOccupancyData(days);
        } else {
          await getOccupancyData();
        }
        setIsLoading(false);
      }
    };

    fetchData();
  }, [data, startDate, endDate, useDatePicker]);

  async function daysBetween(date1: string, date2: string): Promise<number> {
    const ms = Math.abs(+new Date(date2) - +new Date(date1));
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  }

  const getLastXDays = (days: number) => {
    const dates = [];
    const today = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      dates.push(d.toISOString().slice(0, 10)); // 'YYYY-MM-DD'
    }
    return dates;
  };

  const getOccupancyData = async (days: number = 30) => {
    await fetchReportShelterIntakesExits()
      .then((res: any) => {
        const data = res[0].data;
        const chartData = getOccupancyPerDay(data, days);
        setCurrent(chartData);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getOccupancyPerDay = (data: any, days: number) => {
    const daysData = getLastXDays(days);

    return daysData.map((dateStr) => {
      const count = data.filter((user: any) => {
        if (!user.intake_at) return false; // Skip if no intake date
        const intake = new Date(user.intake_at);
        const exit = user.exit_at ? new Date(user.exit_at) : null;
        const day = new Date(dateStr);

        return intake <= day && (!exit || exit > day);
      }).length;

      return { date: dateStr, count };
    });
  };

  const maxScale = Math.max(...current.map((d) => d.count)) + 5 || 10; // Default to 10 if scale is not provided
  const minScale = Math.min(...current.map((d) => d.count)) - 5 || 0; // Default to 10 if scale is not provided

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: `${numberDays} Day Occupancy`,
        font: {
          size: 18,
          weight: 500,
        },
      },
    },
    scales: {
      y: {
        min: minScale,
        max: maxScale,
        ticks: {
          font: {
            size: 14,
          },
        },
      },
      x: {
        type: "time" as const,
        time: {
          unit: numberDays >= 180 ? ("month" as const) : ("day" as const),
          displayFormats: { month: "MMM yyyy" },
          tooltipFormat: "MMM d, yyyy",
        },
        ticks: {
          maxTicksLimit: 8, // Limit the number of ticks to prevent overcrowding
          maxRotation: 30, // Rotate labels at a 45-degree angle
          font: {
            size: 14,
          },
        },
      },
    },
  };

  const chartData = {
    labels: current?.map((item: any) => item?.date),
    datasets: [
      {
        label: "Population",
        data: current?.map((item: any) => item?.count),
        borderColor: "rgb(218 , 126 , 57)",
        backgroundColor: "rgba(218 , 126 , 57, 0.5)",
      },
    ],
  };

  if (isLoading) {
    return loadingCard();
  } else {
    return (
      <div>
        <Line options={options} data={chartData} />
      </div>
    );
  }
};
